import { deviceSchema } from "@fatbees/schemas";
import { toast } from "@fatbees/ui-components";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import { QueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { putDevice } from "../api";

const REFETCH_INTERVAL = 1000 * 60 * 15; // 15 minutes
const QUERY_KEY = ["devices"];

export function useDevicesCollectionInstance(queryClient: QueryClient) {
  useEffect(() => {
    const interval = setInterval(() => {
      queryClient.refetchQueries({ queryKey: QUERY_KEY });
    }, REFETCH_INTERVAL);

    return () => clearInterval(interval);
  }, [queryClient]);

  return useMemo(() => {
    return createCollection(
      queryCollectionOptions({
        id: "devices",
        queryKey: QUERY_KEY,
        queryFn: async () => {
          const response = await fetch("/api/devices");
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const body = await response.json();

          return body.map((device: unknown) => deviceSchema.parse(device));
        },
        queryClient,
        getKey: (item) => item.id,
        schema: deviceSchema,
        onUpdate: async ({ transaction, collection }) => {
          try {
            await Promise.all(
              transaction.mutations.map(async ({ modified }) => {
                await putDevice(modified);

                collection.utils.writeUpdate(modified);
              }),
            );
          } catch (error) {
            toast({ title: "Could not save device", type: "error" });
            throw error;
          }

          return { refetch: false };
        },
      }),
    );
  }, [queryClient]);
}

export type DevicesCollection = ReturnType<typeof useDevicesCollectionInstance>;
